// ---------------------------------------------------------------------------
// Eat the click that a dismissing press is about to produce.
//
// **The bug this exists for, in Isaac's words:** open the host menu on one
// player's row on a phone, tap the darkened area above the sheet to get out of
// it — which does close it — and if the tap happened to land over another
// player's three-dots button, that player's menu opens on the way out. One
// gesture, meant as "get me out of this", read as "get me out of this AND press
// that".
//
// It is not the scrim failing to catch the tap. The scrim is a real element
// covering the screen and the press does land on it. The trouble is that a
// press and a click are two events at two different times: the menu closes on
// `pointerdown`, React unmounts the scrim before the finger comes up, and the
// browser then hit-tests the `click` against whatever is under that point NOW.
// The scrim has gone, so the answer is the button that was underneath it.
//
// **Dismissing on `click` instead would fix it and must not be done.** The host
// row menu's dismissal has to stay on `pointerdown` alone, because the bot
// difficulty slider inside it is a drag: one press inside the menu, a stream of
// moves that a thumb takes clean outside it, and a release somewhere else
// entirely. A rule that closes on any press ENDING outside closes the menu in
// the middle of every drag and the value never reaches the room. `CLAUDE.md`
// records that, and `LobbyScreen.test.tsx` drags out to the body and releases
// there specifically to keep it true.
//
// So the press still dismisses, and the click it is about to generate is
// swallowed on the way past. Capture phase at the document, so it is stopped
// before it reaches anything that would act on it.
//
// **Only call this when the press landed on a scrim.** A menu with no backdrop
// — the account dropdown is one — is a different case: there, "outside" is
// whatever the person was actually aiming at, and tapping a link while the menu
// is open should follow the link. The darkened area is what makes the tap
// unambiguous, so having one is the condition for eating the click.
// ---------------------------------------------------------------------------

/**
 * How long to wait for the click before giving up, in ms.
 *
 * Generous, because a press held for a moment before release still produces one
 * and swallowing it is still right. The backstop matters less than it looks: a
 * new gesture cancels the wait as well, so the only thing this bounds is a press
 * that never releases at all.
 */
const GIVE_UP_MS = 1500;

export function swallowNextClick(): void {
  let done = false;

  const cleanup = () => {
    if (done) return;
    done = true;
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('pointerdown', onNextPress, true);
    window.clearTimeout(timer);
  };

  const onClick = (event: MouseEvent) => {
    // Both, and neither is redundant. `stopPropagation` keeps it from any
    // handler; `preventDefault` keeps the browser from following an `<a href>`,
    // which no handler is involved in.
    event.stopPropagation();
    event.preventDefault();
    cleanup();
  };

  // A fresh press means the gesture we were waiting on is over and whatever
  // comes next is somebody's real second tap. Stop listening rather than eat it.
  const onNextPress = () => cleanup();

  const timer = window.setTimeout(cleanup, GIVE_UP_MS);
  document.addEventListener('click', onClick, true);
  document.addEventListener('pointerdown', onNextPress, true);
}
